import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Trash2, Image, Calendar, HardDrive, CheckSquare, Square, ExternalLink, Music, Video, File, AlertCircle, Loader2, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { useOrphanedMediaDetector } from '@/hooks/useOrphanedMediaDetector';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import LazyImageWithSpinner from './LazyImageWithSpinner';
import OrphanedMediaStats from './OrphanedMediaStats';
import OrphanedMediaFilters from './OrphanedMediaFilters';
import OrphanedMediaProgress from './OrphanedMediaProgress';
import { useToast } from '@/hooks/use-toast';
import { debugLogger } from '@/utils/debugLogger';
import { getSignedMediaUrl } from '@/utils/signedMediaUtils';

interface OrphanedFile {
  name: string;
  path: string;
  size: number;
  created_at: string;
  bucket: string;
  url: string | null;
  isOrphaned: boolean;
  fileType: 'photo' | 'audio' | 'video' | 'other';
}

// Componente especializado para chat_media que precisa de signed URLs
const ChatMediaPreview: React.FC<{ file: OrphanedFile }> = ({ file }) => {
  const { signedUrl, isLoading, error } = useSignedMediaUrl({
    mediaUrl: file.path,
    contentType: 'image'
  });

  if (isLoading) {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-white/60 animate-spin" />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <AlertCircle className="h-6 w-6 text-red-500" />
      </div>
    );
  }

  return (
    <img 
      src={signedUrl} 
      alt={file.name}
      className="w-16 h-16 flex-shrink-0 object-cover rounded-lg border border-white/10"
      onError={(e) => {
        e.currentTarget.src = '';
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

// Componente para fotos públicas que não precisam de signed URLs
const PublicPhotoPreview: React.FC<{ file: OrphanedFile }> = ({ file }) => {
  const [imageError, setImageError] = useState(false);

  if (!file.url || imageError) {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <ImageIcon className="h-6 w-6 text-white/60" />
      </div>
    );
  }

  return (
    <img 
      src={file.url} 
      alt={file.name}
      className="w-16 h-16 flex-shrink-0 object-cover rounded-lg border border-white/10"
      onError={() => setImageError(true)}
    />
  );
};

// Componente principal que roteia para o preview apropriado
const ImagePreview: React.FC<{ file: OrphanedFile }> = ({ file }) => {
  // 1. Para tipos não-imagem, mostrar ícone apropriado (SEM useSignedMediaUrl)
  if (file.fileType === 'audio') {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <Music className="h-8 w-8 text-white/60" />
      </div>
    );
  }

  if (file.fileType === 'video') {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <Video className="h-8 w-8 text-white/60" />
      </div>
    );
  }

  if (file.fileType === 'other') {
    return (
      <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <File className="h-8 w-8 text-white/60" />
      </div>
    );
  }

  // 2. Para fotos do bucket profile_photos (públicas)
  if (file.bucket === 'profile_photos') {
    return <PublicPhotoPreview file={file} />;
  }

  // 3. Para fotos do bucket chat_media (privadas)
  if (file.bucket === 'chat_media') {
    return <ChatMediaPreview file={file} />;
  }

  // 4. Fallback genérico para outros buckets
  return (
    <div className="w-16 h-16 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
      <ImageIcon className="h-6 w-6 text-white/60" />
    </div>
  );
};

// Função para normalizar nome do arquivo e extrair base name
const normalizeFileName = (fileName: string): string => {
  // Remover extensão
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  
  // Extrair base name que geralmente está no formato: timestamp-hash
  // Exemplo: "1749660093614-diz46uj9vmr-ultra-thumb" -> "1749660093614-diz46uj9vmr"
  const baseNameMatch = nameWithoutExt.match(/^(\d+[-_][a-zA-Z0-9]+)/);
  if (baseNameMatch) {
    return baseNameMatch[1];
  }
  
  // Se não conseguir extrair, retornar o nome sem extensão
  return nameWithoutExt;
};

const OrphanedMediaManager: React.FC = () => {
  const {
    orphanedFiles,
    stats,
    isLoading,
    isDeleting,
    scanProgress,
    deleteProgress,
    batchInfo,
    scanOrphanedMedia,
    deleteOrphanedFiles,
    MAX_SELECTION
  } = useOrphanedMediaDetector();
  
  const [selectedFiles, setSelectedFiles] = useState<OrphanedFile[]>([]);
  const [fileTypeFilter, setFileTypeFilter] = useState<'all' | 'photo' | 'audio' | 'video' | 'other'>('all');
  const [sortField, setSortField] = useState<'name' | 'size' | 'date' | 'type'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { toast } = useToast();

  // Filtrar e ordenar arquivos
  const filteredAndSortedFiles = useMemo(() => {
    // 1. Filtrar por tipo
    let filtered = orphanedFiles;
    if (fileTypeFilter !== 'all') {
      filtered = filtered.filter(f => f.fileType === fileTypeFilter);
    }
    
    // 2. Ordenar
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'type':
          comparison = a.fileType.localeCompare(b.fileType);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [orphanedFiles, fileTypeFilter, sortField, sortDirection]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelectFile = (file: OrphanedFile) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.path === file.path);
      if (isSelected) {
        return prev.filter(f => f.path !== file.path);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === filteredAndSortedFiles.length) {
      setSelectedFiles([]);
    } else {
      const toSelect = filteredAndSortedFiles.slice(0, MAX_SELECTION);
      if (filteredAndSortedFiles.length > MAX_SELECTION) {
        toast({
          title: "Limite de Seleção",
          description: `Apenas ${MAX_SELECTION} arquivos foram selecionados por questão de performance.`,
        });
      }
      setSelectedFiles(toSelect);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.length === 0) return;

    try {
      debugLogger.debug('[OrphanedMediaManager] Starting deletion of selected files:', selectedFiles.length);
      
      await deleteOrphanedFiles(selectedFiles);

      setSelectedFiles([]);
    } catch (error) {
      debugLogger.error('[OrphanedMediaManager] Error deleting files:', error);
      toast({
        title: "Erro na Limpeza",
        description: "Ocorreu um erro ao remover os arquivos. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  const handleOpenFile = async (file: OrphanedFile) => {
    try {
      if (file.bucket === 'chat_media') {
        // Para chat_media, obter URL assinada
        const signedUrl = await getSignedMediaUrl(file.path, 3600);
        if (signedUrl) {
          window.open(signedUrl, '_blank');
        } else {
          toast({
            title: "Erro",
            description: "Não foi possível abrir o arquivo",
            variant: "destructive"
          });
        }
      } else if (file.url) {
        // Para profile_photos, usar URL pública
        window.open(file.url, '_blank');
      }
    } catch (error) {
      debugLogger.error('[OrphanedMediaManager] Error opening file:', error);
      toast({
        title: "Erro",
        description: "Não foi possível abrir o arquivo",
        variant: "destructive"
      });
    }
  };

  const handleScanClick = () => {
    scanOrphanedMedia();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Limpeza de Mídias Órfãs
          </h2>
          <p className="text-white/60 text-sm">
            Identifique e remova arquivos não utilizados para liberar espaço
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleScanClick}
            disabled={isLoading}
            className="bg-luxury-red hover:bg-luxury-red/90"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Analisar
          </Button>
          {batchInfo.hasMore && (
            <Button
              onClick={() => scanOrphanedMedia(true)}
              disabled={isLoading}
              variant="outline"
              className="border-white/30 text-white"
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              Mais ({batchInfo.currentBatch}/{batchInfo.totalBatches})
            </Button>
          )}
        </div>
      </div>

      {/* Progress */}
      {isLoading && <OrphanedMediaProgress progress={scanProgress} isActive={isLoading} type="scan" />}
      {isDeleting && <OrphanedMediaProgress progress={deleteProgress} isActive={isDeleting} type="delete" />}

      {/* Batch Info */}
      {batchInfo.totalBatches > 1 && stats.totalFiles > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/70">
              Mostrando {stats.totalFiles} de ~{batchInfo.totalBatches * batchInfo.itemsPerBatch} arquivos
            </span>
            <Badge variant="secondary" className="bg-white/10">
              Batch {batchInfo.currentBatch}/{batchInfo.totalBatches}
            </Badge>
          </div>
        </div>
      )}

      {/* Stats */}
      <OrphanedMediaStats stats={stats} isLoading={isLoading} />

      {/* Filters */}
      {orphanedFiles.length > 0 && (
        <OrphanedMediaFilters
          fileTypeFilter={fileTypeFilter}
          onFileTypeFilterChange={setFileTypeFilter}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortFieldChange={setSortField}
          onSortDirectionToggle={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
          filteredCount={filteredAndSortedFiles.length}
        />
      )}

      {/* Actions */}
      {filteredAndSortedFiles.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-white/5 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSelectAll}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
            >
              {selectedFiles.length === filteredAndSortedFiles.length ? (
                <CheckSquare className="h-4 w-4 mr-2" />
              ) : (
                <Square className="h-4 w-4 mr-2" />
              )}
              {selectedFiles.length === filteredAndSortedFiles.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
            </Button>
            {selectedFiles.length > 0 && (
              <Badge variant="secondary">
                {selectedFiles.length} selecionado{selectedFiles.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          {selectedFiles.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="sm"
                  disabled={isDeleting}
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remover Selecionados
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-black border-white/20">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">
                    Confirmar Remoção
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-white/70">
                    Você está prestes a remover permanentemente {selectedFiles.length} arquivo(s) órfão(s).
                    Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="text-white bg-white/10 hover:bg-white/20">
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteSelected}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Remover Arquivos
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {/* Files List */}
      {filteredAndSortedFiles.length > 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">
              Arquivos Órfãos Encontrados ({filteredAndSortedFiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              {filteredAndSortedFiles.map((file) => {
                const isSelected = selectedFiles.some(f => f.path === file.path);
                const normalizedName = normalizeFileName(file.name);
                
                return (
                  <div 
                    key={file.path} 
                    className={`
                      flex items-center gap-3 p-3 rounded-lg border transition-all
                      ${isSelected 
                        ? 'bg-luxury-red/20 border-luxury-red/50' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }
                    `}
                  >
                    {/* Checkbox */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleSelectFile(file)}
                        className="flex items-center justify-center"
                      >
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-luxury-red" />
                        ) : (
                          <Square className="h-5 w-5 text-white/40 hover:text-white/60" />
                        )}
                      </button>
                    </div>

                    {/* Image Preview */}
                    <ImagePreview file={file} />

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {file.fileType === 'photo' && <Image className="h-4 w-4 text-white/60" />}
                        {file.fileType === 'audio' && <Music className="h-4 w-4 text-white/60" />}
                        {file.fileType === 'video' && <Video className="h-4 w-4 text-white/60" />}
                        {file.fileType === 'other' && <File className="h-4 w-4 text-white/60" />}
                        <span className="text-sm font-medium text-white truncate">
                          {file.name}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 text-xs text-white/60 mb-1">
                        <div className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          {formatFileSize(file.size)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(file.created_at)}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {file.bucket}
                        </Badge>
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          {file.fileType === 'photo' && <Image className="h-3 w-3" />}
                          {file.fileType === 'audio' && <Music className="h-3 w-3" />}
                          {file.fileType === 'video' && <Video className="h-3 w-3" />}
                          {file.fileType === 'other' && <File className="h-3 w-3" />}
                          {file.fileType}
                        </Badge>
                      </div>
                      
                      {/* Base name info */}
                      <div className="text-xs text-white/40">
                        Base: {normalizedName}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleOpenFile(file)}
                        variant="ghost"
                        size="sm"
                        className="text-white/60 hover:text-white hover:bg-white/10"
                        title="Abrir arquivo"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        !isLoading && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Image className="h-12 w-12 text-white/40 mb-4" />
              <p className="text-white/60 text-center">
                {stats.totalFiles > 0 
                  ? 'Nenhum arquivo órfão encontrado! 🎉' 
                  : 'Clique em "Analisar" para verificar arquivos órfãos'
                }
              </p>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
};

export default OrphanedMediaManager;
